/**
 * Tool registry — composable mapping from tool names to React renderers.
 *
 * Host apps register their domain cards once at module load via
 * `registerTool([...names], renderer)`. The block's `<ToolCallCard>`
 * looks up renderers by tool name and falls back to a generic JSON
 * preview when nothing is registered.
 *
 * The registry is module-scoped and process-singleton — this is
 * intentional. Vercel AI SDK tool names can vary (camelCase / kebab /
 * snake), so registrations accept multiple aliases per renderer.
 *
 * Tree-shake survival: this file exports primitives only and never
 * auto-registers anything. Host apps must import their registration
 * files explicitly AND consume the return value of an init function so
 * the bundler can't elide the side-effect imports. See
 * `apps/connect/.../chat/tool-cards/registrations.tsx` for the pattern.
 */

import type {
  ToolInvocation,
  ToolLoadingRenderer,
  ToolRenderer,
  ToolRendererOptions,
} from "./types"

export type { ToolInvocation, ToolLoadingRenderer, ToolRenderer, ToolRendererOptions }

const toolRegistry: Record<string, ToolRenderer> = {}
const toolLoadingRegistry: Record<string, ToolLoadingRenderer> = {}

export function registerTool(names: string[], renderer: ToolRenderer): void {
  names.forEach((name) => {
    toolRegistry[name] = renderer
  })
}

export function registerToolLoading(
  names: string[],
  renderer: ToolLoadingRenderer,
): void {
  names.forEach((name) => {
    toolLoadingRegistry[name] = renderer
  })
}

export function getToolRenderer(toolName: string): ToolRenderer | undefined {
  return toolRegistry[toolName]
}

export function getToolLoadingRenderer(
  toolName: string,
): ToolLoadingRenderer | undefined {
  return toolLoadingRegistry[toolName]
}

export function hasToolRenderer(toolName: string): boolean {
  return toolName in toolRegistry
}

export function getRegisteredToolNames(): string[] {
  return Object.keys(toolRegistry)
}

export { toolRegistry }
